import React from 'react';
import {
  Box,
  Button,
  Chip,
  Divider,
  IconButton,
  ListItemIcon,
  Menu,
  MenuItem,
  Stack,
  Typography,
} from '@mui/material';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import EditRoundedIcon from '@mui/icons-material/EditRounded';
import ContentCopyRoundedIcon from '@mui/icons-material/ContentCopyRounded';
import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import LifeObjectRenderer from '../experiences/LifeObjectRenderer';
import MobileAppPreview from './MobileAppPreview';
import { lifeTaggerDesign } from '../design/tokens';

const experiences = [
  { id: 'mobile', label: 'Mobile app', color: '#D96B50' },
  { id: 'web', label: 'Web browser', color: '#5F7C70' },
  { id: 'ar', label: 'AR / glasses', color: '#A28A68' },
];

const signalNames = {
  qr: 'LifeCode',
  geo: 'Location',
  'geo-point': 'Location',
  audio: 'Audio fingerprint',
  url: 'URL',
  nfc: 'NFC',
  ble: 'Nearby beacon',
  time: 'Time',
  identity: 'Audience',
  spatial: 'Spatial location',
};

const experienceDetails = {
  mobile: {
    eyebrow: 'Mobile preview',
    title: 'Mobile app',
    status: 'Ready',
    capabilities: ['View', 'Listen', 'Open links'],
    note: 'Mirrors the mobile app’s moment detail view. Save, share, and replies are read-only in this preview.',
  },
  web: {
    eyebrow: 'Web preview',
    title: 'Web browser',
    status: 'Ready',
    capabilities: ['Responsive layout', 'Optional location', 'Play audio'],
    note: 'Your saved content as a browser experience.',
  },
  ar: {
    eyebrow: 'AR preview',
    title: 'AR / glasses',
    status: 'Ready',
    capabilities: ['Floating view', 'Voice', 'Continue on phone'],
    note: 'Preview your content in an AR layout. This does not test room recognition or device compatibility.',
  },
};

const audienceLabel = (audience = {}) => {
  if (audience.visibility === 'public') return 'Available to everyone';
  if (audience.visibility === 'followers') return 'Available to subscribers';
  if (audience.visibility === 'named-users')
    return 'Available to selected people';
  if (audience.visibility === 'private') return 'Only you';
  return 'Available to your saved audience';
};

const lifecycleLabel = (lifecycle = {}) => {
  const status = lifecycle.status || 'draft';
  return status === 'active'
    ? 'Published'
    : `${status.charAt(0).toUpperCase()}${status.slice(1)}`;
};

const signalDetail = (anchor) =>
  (/^migrated signal$/i.test(anchor.name || '') ? null : anchor.name) ||
  anchor.label ||
  anchor.identifier ||
  (anchor.type === 'geo-point' ? 'Saved place' : signalNames[anchor.type]) ||
  'Connected signal';

function ExperienceCanvas({ lifeObject, experience }) {
  const isAr = experience === 'ar';
  const phoneFrame = React.useRef(null);
  const [phoneHeight, setPhoneHeight] = React.useState(812);
  React.useLayoutEffect(() => {
    const frame = phoneFrame.current;
    if (!frame) return undefined;
    const fit = () => {
      const top = frame.getBoundingClientRect().top;
      const available = window.innerHeight - Math.max(0, top) - 24;
      const width = frame.parentElement.clientWidth;
      setPhoneHeight(
        Math.min(
          812,
          Math.max(0, available),
          width > 0 ? (width * 812) / 375 : 812
        )
      );
    };
    fit();
    window.addEventListener('resize', fit);
    const observer =
      typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(fit);
    observer?.observe(frame.parentElement);
    return () => {
      window.removeEventListener('resize', fit);
      observer?.disconnect();
    };
  }, [experience]);

  if (experience === 'mobile') {
    return (
      <Box sx={{ py: 1 }}>
        <Typography
          variant="overline"
          sx={{
            color: '#706C65',
            letterSpacing: '.12em',
          }}
        >
          Mobile preview
        </Typography>
        <Box
          ref={phoneFrame}
          data-testid="mobile-device-container"
          style={{ height: phoneHeight, width: (phoneHeight * 375) / 812 }}
          sx={{ mt: 2, mx: 'auto', position: 'relative' }}
        >
          <Box
            data-testid="mobile-device-frame"
            style={{ transform: `scale(${phoneHeight / 812})` }}
            sx={{
              width: 375,
              height: 812,
              transformOrigin: 'top left',
              boxSizing: 'border-box',
              bgcolor: '#0B0B0B',
              border: '1px solid #1F1F1F',
              borderRadius: '56px',
              p: '8px',
              boxShadow: '0 16px 32px rgba(0,0,0,.16)',
              position: 'relative',
            }}
          >
            <Box
              sx={{
                position: 'absolute',
                right: 3,
                top: '26%',
                width: 4,
                height: 44,
                bgcolor: '#1F1F1F',
                borderRadius: 0.5,
              }}
            />
            <Box
              sx={{
                height: '100%',
                borderRadius: '44px',
                bgcolor: '#090909',
                overflow: 'hidden',
                position: 'relative',
              }}
            >
              <Box
                component="img"
                src="/figma-preview/dynamic-island.svg"
                alt=""
                sx={{
                  position: 'absolute',
                  zIndex: 2,
                  top: 10,
                  left: '50%',
                  transform: 'translateX(-50%)',
                  width: 126,
                  height: 34,
                }}
              />
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                sx={{ height: 36, px: 3, color: '#F4EFE7' }}
              >
                <Typography sx={{ fontSize: 13, fontWeight: 600 }}>
                  9:41
                </Typography>
                <Stack direction="row" spacing={0.5}>
                  {['ios-signal', 'ios-wifi', 'ios-battery'].map((name) => (
                    <Box
                      key={name}
                      component="img"
                      src={`/figma-preview/${name}.svg`}
                      alt=""
                      sx={{
                        width: name === 'ios-battery' ? 20 : 16,
                        height: 12,
                        filter: 'brightness(0) invert(1)',
                      }}
                    />
                  ))}
                </Stack>
              </Stack>
              <Box
                data-testid="device-content-mobile"
                sx={{
                  contain: 'layout paint',
                  height: 'calc(100% - 36px)',
                  overflow: 'auto',
                  bgcolor: '#090909',
                  '& img, & video': { maxWidth: '100%' },
                }}
              >
                <MobileAppPreview lifeObject={lifeObject} />
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>
    );
  }

  if (experience === 'web') {
    return (
      <Box>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ mb: 2 }}
        >
          <Typography
            sx={{ fontFamily: lifeTaggerDesign.type.headline, fontSize: 28 }}
          >
            Web Browser
          </Typography>
          <Chip
            size="small"
            label="Preview"
            sx={{
              bgcolor: '#597367',
              color: '#fff',
              borderRadius: '4px',
              fontFamily: 'monospace',
              textTransform: 'uppercase',
            }}
          />
        </Stack>
        <Box
          sx={{
            height: 450,
            bgcolor: '#FFF',
            border: '1px solid #D8D1C5',
            borderRadius: '16px',
            overflow: 'hidden',
            boxShadow: '0 8px 16px rgba(0,0,0,.06)',
          }}
        >
          <Stack
            direction="row"
            spacing={1.5}
            alignItems="center"
            sx={{
              height: 40,
              px: 2,
              bgcolor: '#EFECE6',
              borderBottom: '1px solid #D8D1C5',
            }}
          >
            <Stack direction="row" spacing={0.6}>
              {['#FC675D', '#FDBC40', '#34C84A'].map((color) => (
                <Box
                  key={color}
                  sx={{ width: 8, height: 8, bgcolor: color, borderRadius: 99 }}
                />
              ))}
            </Stack>
            <Box
              sx={{
                width: 340,
                maxWidth: '70%',
                px: 1.5,
                py: 0.5,
                bgcolor: '#FFF',
                borderRadius: '6px',
                color: '#706C65',
                fontSize: 11,
              }}
            >
              LifeObject preview
            </Box>
          </Stack>
          <Box
            data-testid="device-content-web"
            sx={{
              height: 'calc(100% - 40px)',
              overflow: 'auto',
              minWidth: 0,
              contain: 'layout paint',
            }}
          >
            <LifeObjectRenderer lifeObject={lifeObject} unframed />
          </Box>
        </Box>
        <Typography sx={{ mt: 2, fontWeight: 600, fontSize: 13 }}>
          What people can experience
        </Typography>
        <Typography sx={{ mt: 0.75, color: '#706C65', fontSize: 13 }}>
          {lifeObject.content?.length || 0} content item
          {lifeObject.content?.length === 1 ? '' : 's'} available. Visitors tap
          when they are ready to hear audio.
        </Typography>
      </Box>
    );
  }

  if (isAr) {
    return (
      <Box>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ mb: 2 }}
        >
          <Typography
            sx={{ fontFamily: lifeTaggerDesign.type.headline, fontSize: 28 }}
          >
            AR / Glasses
          </Typography>
          <Chip
            size="small"
            label="Preview"
            sx={{ bgcolor: '#294438', color: '#fff', borderRadius: 0.5 }}
          />
        </Stack>
        <Box
          sx={{
            height: 450,
            borderRadius: 2,
            overflow: 'hidden',
            position: 'relative',
            backgroundImage:
              'linear-gradient(rgba(0,0,0,.08), rgba(0,0,0,.08)), url(/figma-preview/ar-cafe.png)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            boxShadow: '0 10px 28px -10px rgba(0,0,0,.22)',
          }}
        >
          <Box
            sx={{
              position: 'absolute',
              left: '50%',
              top: '46%',
              transform: 'translate(-50%, -50%)',
              width: 340,
              maxWidth: '78%',
              p: 2.5,
              borderRadius: 2.5,
              border: '1px solid rgba(255,255,255,.8)',
              bgcolor: 'rgba(255,255,255,.54)',
              backdropFilter: 'blur(14px)',
              boxShadow: '0 14px 34px -10px rgba(0,0,0,.25)',
            }}
          >
            <Stack direction="row" spacing={1} alignItems="center">
              <Box
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  bgcolor: '#597367',
                }}
              />
              <Typography
                sx={{ fontFamily: 'monospace', fontSize: 10, color: '#706C65' }}
              >
                LIFEOBJECT FOUND HERE
              </Typography>
            </Stack>
            <Typography
              sx={{
                mt: 1,
                fontFamily: lifeTaggerDesign.type.headline,
                fontSize: 22,
                color: '#1C1A19',
              }}
            >
              {lifeObject.name}
            </Typography>
            <Box
              data-testid="device-content-ar"
              sx={{
                contain: 'layout paint',
                mt: 1,
                maxHeight: 280,
                overflow: 'auto',
                bgcolor: '#fff',
                color: '#1C1A19',
              }}
            >
              <LifeObjectRenderer lifeObject={lifeObject} unframed />
            </Box>
          </Box>
          <Stack
            direction="row"
            spacing={0.75}
            alignItems="center"
            sx={{
              position: 'absolute',
              bottom: 27,
              left: '50%',
              transform: 'translateX(-50%)',
            }}
          >
            <Box
              sx={{
                width: 12,
                height: 12,
                borderRadius: '50%',
                border: '1px solid #fff',
                bgcolor: 'rgba(255,255,255,.35)',
              }}
            />
            <Typography
              sx={{ color: '#fff', fontFamily: 'monospace', fontSize: 10 }}
            >
              Looking here
            </Typography>
          </Stack>
        </Box>
        <Typography sx={{ mt: 2, fontWeight: 600, fontSize: 13 }}>
          What people can experience
        </Typography>
        <Typography sx={{ mt: 0.75, color: '#706C65', fontSize: 13 }}>
          The LifeObject appears in the environment. Longer documents and
          detailed actions can continue on a paired phone.
        </Typography>
      </Box>
    );
  }
  return null;
}

class PreviewErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null, retryKey: 0 };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  retry = () => {
    this.setState((state) => ({ error: null, retryKey: state.retryKey + 1 }));
  };

  render() {
    if (this.state.error) {
      return (
        <Stack
          alignItems="center"
          justifyContent="center"
          spacing={2}
          sx={{ minHeight: 520, textAlign: 'center', px: 3 }}
        >
          <Box
            sx={{
              display: 'grid',
              placeItems: 'center',
              width: 64,
              height: 64,
              border: '1px dashed #D96B50',
              borderRadius: '50%',
              color: '#D96B50',
            }}
          >
            <RefreshRoundedIcon />
          </Box>
          <Typography
            sx={{ fontFamily: lifeTaggerDesign.type.headline, fontSize: 28 }}
          >
            Preview could not be loaded
          </Typography>
          <Typography sx={{ maxWidth: 460, color: '#706C65', fontSize: 14 }}>
            This is usually temporary. The LifeObject and its discovery signals
            remain available.
          </Typography>
          <Button
            variant="contained"
            onClick={this.retry}
            sx={{ bgcolor: '#D96B50', borderRadius: 99, px: 3 }}
          >
            Try again
          </Button>
          <Button
            onClick={this.props.onEdit}
            sx={{ color: '#706C65', textDecoration: 'underline' }}
          >
            Or open in editor →
          </Button>
        </Stack>
      );
    }

    return (
      <React.Fragment key={this.state.retryKey}>
        {this.props.children}
      </React.Fragment>
    );
  }
}

export default function LifeObjectExperiencePreview({
  lifeObject,
  previewUrl,
  onClose,
  onExit,
  onEdit,
  onDuplicate,
  saving,
}) {
  const [experience, setExperience] = React.useState('mobile');
  const [overflowAnchor, setOverflowAnchor] = React.useState(null);
  const [discoveryAnchor, setDiscoveryAnchor] = React.useState(null);
  const [selectedSignalIndex, setSelectedSignalIndex] = React.useState(0);
  if (!lifeObject) return null;
  const anchors = lifeObject.anchors || [];
  const primarySignal = anchors[selectedSignalIndex] || anchors[0];
  const contentCount = lifeObject.content?.length || 0;

  const signalCard = (anchor, index) => (
    <Stack
      key={anchor.id || `${anchor.type}-${index}`}
      direction="row"
      spacing={2}
      alignItems="center"
      sx={{
        px: 2,
        py: 1.75,
        bgcolor: '#FFFCF7',
        border: '1px solid #D8D1C5',
        borderRadius: '12px',
      }}
    >
      <Box
        sx={{
          display: 'grid',
          placeItems: 'center',
          width: 36,
          height: 36,
          bgcolor: '#D6E1D8',
          borderRadius: '8px',
        }}
      >
        <Box
          component="img"
          src={
            anchor.type === 'geo' || anchor.type === 'geo-point'
              ? '/figma-preview/location.svg'
              : '/figma-preview/qr.svg'
          }
          alt=""
          sx={{ width: 16, height: 16 }}
        />
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography sx={{ fontSize: 14, fontWeight: 600 }}>
          {signalNames[anchor.type] || anchor.type}
        </Typography>
        <Typography noWrap sx={{ color: '#706C65', fontSize: 13 }}>
          {signalDetail(anchor)}
        </Typography>
      </Box>
      <Stack direction="row" spacing={0.75} alignItems="center">
        <Box
          sx={{ width: 8, height: 8, bgcolor: '#597367', borderRadius: 99 }}
        />
        <Typography sx={{ color: '#597367', fontSize: 13 }}>Active</Typography>
      </Stack>
    </Stack>
  );

  return (
    <Box
      sx={{
        minHeight: '100%',
        bgcolor: '#F4F0E8',
        color: '#1C1A19',
        fontFamily: lifeTaggerDesign.type.body,
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{
          height: 80,
          px: 6,
          bgcolor: '#FAF9F7',
          borderBottom: '1px solid #D8D1C5',
        }}
      >
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Box
            component="img"
            src="/figma-preview/fingerprint.svg"
            alt=""
            sx={{ width: 28, height: 28 }}
          />
          <Typography
            sx={{
              fontFamily: lifeTaggerDesign.type.headline,
              fontStyle: 'italic',
              fontSize: 22,
              color: '#294438',
            }}
          >
            LifeTagger
          </Typography>
          <Typography sx={{ color: '#A09C94', fontSize: 14 }}>/</Typography>
          <Typography sx={{ color: '#706C65', fontSize: 14 }}>
            Collection / {lifeObject.name}
          </Typography>
        </Stack>
        <Stack direction="row" spacing={0.5}>
          <IconButton aria-label="Back to LifeObject preview" onClick={onClose}>
            <ArrowBackRoundedIcon />
          </IconButton>
          <IconButton aria-label="Close preview" onClick={onExit || onClose}>
            <CloseRoundedIcon />
          </IconButton>
        </Stack>
      </Stack>
      <Box
        sx={{
          borderBottom: '1px solid #D8D1C5',
          px: { xs: 2, md: 10 },
          pt: 4,
          pb: 3,
        }}
      >
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={2}
          alignItems={{ md: 'center' }}
          justifyContent="space-between"
        >
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              sx={{
                color: '#D96B50',
                fontSize: 11,
                fontWeight: 600,
                textTransform: 'uppercase',
              }}
            >
              Previewing LifeObject
            </Typography>
            <Typography
              sx={{
                fontFamily: lifeTaggerDesign.type.headline,
                fontSize: { xs: 30, md: 40 },
                fontWeight: 500,
                lineHeight: 1.15,
                mt: 0.75,
              }}
            >
              {lifeObject.name}
            </Typography>
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              flexWrap="wrap"
              useFlexGap
              sx={{ mt: 1 }}
            >
              <Chip
                size="small"
                label={lifecycleLabel(lifeObject.lifecycle)}
                icon={
                  <Box
                    sx={{
                      width: 8,
                      height: 8,
                      bgcolor: '#597367',
                      borderRadius: 99,
                    }}
                  />
                }
                sx={{ bgcolor: '#EFECE6', color: '#294438', fontWeight: 600 }}
              />
              <Typography variant="body2" color="text.secondary">
                {audienceLabel(lifeObject.audience)} · Available anytime
              </Typography>
            </Stack>
          </Box>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Button
              startIcon={<EditRoundedIcon />}
              variant="contained"
              onClick={() => onEdit(lifeObject)}
              sx={{
                bgcolor: '#D96B50',
                borderRadius: 99,
                px: 2.5,
                '&:hover': { bgcolor: '#C25D46' },
              }}
            >
              Edit LifeObject
            </Button>
            {previewUrl && (
              <Button
                component="a"
                href={previewUrl}
                target="_blank"
                rel="noreferrer"
                sx={{ color: '#D96B50' }}
              >
                Open Experience →
              </Button>
            )}
            {!previewUrl && (
              <Stack direction="row" spacing={0.75} alignItems="center">
                <Button disabled sx={{ px: 0.5 }}>
                  Open Experience →
                </Button>
                <Typography sx={{ color: '#A09C94', fontSize: 11 }}>
                  (No public URL)
                </Typography>
              </Stack>
            )}
            <Button
              aria-label="More LifeObject actions"
              aria-haspopup="menu"
              aria-expanded={Boolean(overflowAnchor)}
              onClick={(event) => setOverflowAnchor(event.currentTarget)}
              sx={{
                minWidth: 42,
                width: 42,
                height: 42,
                border: '1px solid #D8D1C5',
                borderRadius: 99,
                color: '#1C1A19',
              }}
            >
              •••
            </Button>
            <Menu
              anchorEl={overflowAnchor}
              open={Boolean(overflowAnchor)}
              onClose={() => setOverflowAnchor(null)}
              PaperProps={{
                sx: {
                  mt: 1,
                  width: 220,
                  border: '1px solid #EAE6DF',
                  borderRadius: 2,
                  boxShadow: '0 12px 28px rgba(28,26,25,.14)',
                },
              }}
            >
              <MenuItem
                disabled={saving}
                onClick={() => {
                  setOverflowAnchor(null);
                  onDuplicate?.();
                }}
              >
                <ListItemIcon>
                  <ContentCopyRoundedIcon fontSize="small" />
                </ListItemIcon>
                Duplicate LifeObject
              </MenuItem>
              <MenuItem
                onClick={() => {
                  setOverflowAnchor(null);
                  onClose?.();
                }}
              >
                <ListItemIcon>
                  <ArrowBackRoundedIcon fontSize="small" />
                </ListItemIcon>
                Back to summary
              </MenuItem>
              <Divider sx={{ my: 0.5 }} />
              <MenuItem
                onClick={() => {
                  setOverflowAnchor(null);
                  (onExit || onClose)?.();
                }}
                sx={{ color: '#D96B50' }}
              >
                <ListItemIcon>
                  <CloseRoundedIcon
                    fontSize="small"
                    sx={{ color: '#D96B50' }}
                  />
                </ListItemIcon>
                Close
              </MenuItem>
            </Menu>
          </Stack>
        </Stack>
      </Box>

      <Stack
        direction="row"
        spacing={4}
        flexWrap="wrap"
        useFlexGap
        role="tablist"
        aria-label="Preview environment"
        sx={{
          px: { xs: 2, md: 10 },
          pt: 2,
          borderBottom: '1px solid #EAE6DF',
          bgcolor: '#FAF9F7',
        }}
      >
        {experiences.map(({ id, label }) => (
          <Button
            key={id}
            role="tab"
            aria-selected={experience === id}
            onClick={() => setExperience(id)}
            sx={{
              minWidth: 0,
              px: 0,
              pb: 1.5,
              borderRadius: 0,
              borderBottom:
                experience === id
                  ? '2px solid #D96B50'
                  : '2px solid transparent',
              color: experience === id ? '#1C1A19' : '#706C65',
              fontWeight: experience === id ? 600 : 500,
            }}
          >
            {label}
          </Button>
        ))}
      </Stack>

      <Stack
        direction={{ xs: 'column', lg: 'row' }}
        alignItems={{ lg: 'center' }}
        justifyContent="space-between"
        spacing={2}
        sx={{
          bgcolor: '#F0EBE3',
          borderTop: '1px solid #D8D1C5',
          borderBottom: '1px solid #D8D1C5',
          px: { xs: 2, md: 10 },
          py: 1.75,
        }}
      >
        <Typography sx={{ color: '#4A4642', fontSize: 13 }}>
          Previewing discovery:{' '}
          {primarySignal
            ? `Found through ${
                signalNames[primarySignal.type] || primarySignal.type
              }: ${signalDetail(primarySignal)}`
            : 'No discovery signal is connected yet'}
        </Typography>
        <Button
          size="small"
          endIcon={<KeyboardArrowDownRoundedIcon />}
          disabled={!anchors.length}
          onClick={(event) => setDiscoveryAnchor(event.currentTarget)}
          sx={{ color: '#D96B50' }}
        >
          Change discovery preview
        </Button>
        <Menu
          anchorEl={discoveryAnchor}
          open={Boolean(discoveryAnchor)}
          onClose={() => setDiscoveryAnchor(null)}
          PaperProps={{
            sx: { width: 340, maxWidth: 'calc(100vw - 32px)', borderRadius: 2 },
          }}
        >
          {anchors.map((anchor, index) => (
            <MenuItem
              key={anchor.id || `${anchor.type}-${index}`}
              selected={index === selectedSignalIndex}
              onClick={() => {
                setSelectedSignalIndex(index);
                setDiscoveryAnchor(null);
              }}
              sx={{ py: 1.5, whiteSpace: 'normal' }}
            >
              <Box>
                <Typography sx={{ fontWeight: 600, fontSize: 13 }}>
                  {signalNames[anchor.type] || anchor.type} ·{' '}
                  {signalDetail(anchor)}
                </Typography>
                <Typography sx={{ color: '#706C65', fontSize: 11 }}>
                  Preview how this signal leads people to the LifeObject
                </Typography>
              </Box>
            </MenuItem>
          ))}
        </Menu>
      </Stack>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            xl: 'minmax(420px, 760px) 500px',
          },
          justifyContent: 'center',
          alignItems: 'start',
          gap: 6,
          px: { xs: 2, md: 10 },
          py: 5,
        }}
      >
        <Box>
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            sx={{ mb: 1.5 }}
          >
            <Typography
              sx={{
                color: '#A09C94',
                fontSize: 11,
                fontWeight: 600,
                letterSpacing: 1.2,
              }}
            >
              PREVIEW
            </Typography>
            <Divider sx={{ flex: 1 }} />
          </Stack>
          <PreviewErrorBoundary onEdit={() => onEdit(lifeObject)}>
            <ExperienceCanvas lifeObject={lifeObject} experience={experience} />
          </PreviewErrorBoundary>
        </Box>
        <Stack spacing={3} sx={{ pt: 3 }}>
          <Box
            sx={{
              p: 3.5,
              bgcolor: '#FFF',
              border: '1px solid #D8D1C5',
              borderRadius: '16px',
            }}
          >
            <Typography
              sx={{
                fontFamily: lifeTaggerDesign.type.headline,
                fontSize: 22,
              }}
            >
              {experienceDetails[experience].title} experience
            </Typography>
            <Typography
              sx={{
                mt: 2,
                color: '#706C65',
                fontSize: 14,
                lineHeight: '22px',
              }}
            >
              {experienceDetails[experience].note}
            </Typography>
            <Divider sx={{ my: 2.5 }} />
            {[
              [
                'Content in this LifeObject',
                `${contentCount} of ${contentCount}`,
              ],
              ['Ways to experience it', 'View, listen, open links'],
              ['Ways to discover it', `${anchors.length} connected`],
            ].map(([label, value]) => (
              <Stack
                key={label}
                direction="row"
                justifyContent="space-between"
                sx={{ py: 0.75 }}
              >
                <Typography sx={{ color: '#706C65', fontSize: 13 }}>
                  {label}
                </Typography>
                <Typography sx={{ fontWeight: 600, fontSize: 13 }}>
                  {value}
                </Typography>
              </Stack>
            ))}
          </Box>
          <Stack direction="row" justifyContent="space-between">
            <Typography
              sx={{
                color: '#706C65',
                fontWeight: 600,
                fontSize: 11,
                textTransform: 'uppercase',
              }}
            >
              How people can find it
            </Typography>
            <Typography sx={{ color: '#597367', fontSize: 12 }}>
              {anchors.length} signal{anchors.length === 1 ? '' : 's'} connected
            </Typography>
          </Stack>
          <Stack spacing={1}>
            {anchors.length ? (
              anchors.map(signalCard)
            ) : (
              <Box sx={{ py: 1 }}>
                <Typography color="text.secondary">
                  No discovery signals connected yet.
                </Typography>
                <Button
                  onClick={() => onEdit(lifeObject)}
                  sx={{ mt: 1, px: 0, color: '#D96B50' }}
                >
                  + Connect a signal in the editor
                </Button>
              </Box>
            )}
          </Stack>
        </Stack>
      </Box>
    </Box>
  );
}
